import { useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, FileIcon, Pause, Play, RefreshCw, RotateCcw, Trash2, Upload as UploadIcon } from 'lucide-react';
import { Sheet } from '../ui/Sheet';
import { Button, IconButton } from '../ui/Button';
import { ProgressBar } from '../ui/misc';
import { cn } from '../lib/cn';
import {
  clearFinished,
  formatBytes,
  pause,
  remove,
  reselect,
  resume,
  retry,
  summarize,
  uploadAgain,
  useUploads,
  type UploadItem,
} from './store';

function stateText(it: UploadItem): string {
  switch (it.state) {
    case 'reserving':
      return 'Preparing…';
    case 'queued':
      return 'Waiting…';
    case 'uploading':
      return `${formatBytes(it.offset)} of ${formatBytes(it.size)}${it.speed > 0 ? ` · ${formatBytes(it.speed)}/s` : ''}`;
    case 'paused':
      return `Paused · ${formatBytes(it.offset)} of ${formatBytes(it.size)}`;
    case 'needs-reselect':
      return it.error ?? 'Paused: reselect files to resume';
    case 'checking':
      return `Checking file… ${Math.round((it.checkProgress ?? 0) * 100)}%`;
    case 'done':
      return `Uploaded · ${formatBytes(it.size)}`;
    case 'error':
    case 'refused':
      return it.error ?? 'Failed';
  }
}

function Row({ it }: { it: UploadItem }) {
  const againRef = useRef<HTMLInputElement>(null);
  const reselectRef = useRef<HTMLInputElement>(null);
  const pct = it.size ? (it.offset / it.size) * 100 : 0;
  const bad = it.state === 'error' || it.state === 'refused' || (it.state === 'needs-reselect' && !!it.error);
  return (
    <li className="flex flex-col gap-2 border-b border-divider py-3 last:border-0">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0">
          {it.state === 'done' ? (
            <CheckCircle2 aria-hidden className="size-5 text-success" />
          ) : bad ? (
            <AlertCircle aria-hidden className="size-5 text-danger" />
          ) : (
            <FileIcon aria-hidden className="size-5 text-muted" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-medium text-ink" title={it.name}>
            {it.name}
          </p>
          <p className={cn('text-[13px]', bad ? 'text-danger-ink' : 'text-muted')}>{stateText(it)}</p>
        </div>
        <div className="-my-1 flex shrink-0 items-center">
          {it.state === 'uploading' && (
            <IconButton label={`Pause ${it.name}`} onClick={() => pause(it.key)}>
              <Pause aria-hidden className="size-5" />
            </IconButton>
          )}
          {it.state === 'paused' && (
            <IconButton label={`Resume ${it.name}`} onClick={() => resume(it.key)}>
              <Play aria-hidden className="size-5" />
            </IconButton>
          )}
          {it.state === 'error' && it.file && (
            <IconButton label={`Retry ${it.name}`} onClick={() => retry(it.key)}>
              <RotateCcw aria-hidden className="size-5" />
            </IconButton>
          )}
          {it.state !== 'done' && (
            <IconButton label={`Remove ${it.name}`} onClick={() => void remove(it.key)}>
              <Trash2 aria-hidden className="size-5" />
            </IconButton>
          )}
        </div>
      </div>
      {(it.state === 'uploading' || it.state === 'paused' || it.state === 'needs-reselect' || it.state === 'queued') && it.size > 0 && (
        <ProgressBar value={pct} label={`${it.name} upload progress`} />
      )}
      {it.state === 'checking' && <ProgressBar value={(it.checkProgress ?? 0) * 100} label={`Checking ${it.name}`} />}
      {it.state === 'needs-reselect' && (
        <div className="flex flex-wrap gap-2">
          <input
            ref={reselectRef}
            type="file"
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={(e) => {
              const f = Array.from(e.target.files ?? []);
              e.target.value = '';
              if (f.length) void reselect([it.key], f);
            }}
          />
          <input
            ref={againRef}
            type="file"
            className="sr-only"
            tabIndex={-1}
            aria-hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void uploadAgain(it.key, f);
            }}
          />
          {!it.error && (
            <Button size="sm" variant="outline" icon={<RefreshCw aria-hidden className="size-4" />} onClick={() => reselectRef.current?.click()}>
              Reselect
            </Button>
          )}
          <Button size="sm" variant="outline" icon={<UploadIcon aria-hidden className="size-4" />} onClick={() => againRef.current?.click()}>
            Upload again
          </Button>
        </div>
      )}
    </li>
  );
}

export function UploadQueueSheet({ open, onClose, businessNames }: { open: boolean; onClose: () => void; businessNames?: Record<string, string> }) {
  const all = useUploads();
  const sum = summarize(all);
  const pausedKeys = useMemo(() => all.filter((x) => x.state === 'needs-reselect' && !x.error).map((x) => x.key), [all]);
  const batchInput = useRef<HTMLInputElement>(null);
  const [lastResult, setLastResult] = useState<string | null>(null);
  const groups = useMemo(() => {
    const m = new Map<string, UploadItem[]>();
    for (const it of all) m.set(it.businessId, [...(m.get(it.businessId) ?? []), it]);
    return [...m.entries()];
  }, [all]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Uploads"
      description={
        sum.live
          ? `${sum.live} uploading · ${sum.pct}% of ${formatBytes(sum.total)}`
          : sum.attention
            ? `${sum.attention} need${sum.attention === 1 ? 's' : ''} attention`
            : all.length
              ? 'All uploads finished'
              : undefined
      }
      footer={
        <div className="flex flex-col gap-2 sm:flex-row">
          {pausedKeys.length > 0 && (
            <>
              <input
                ref={batchInput}
                type="file"
                multiple
                className="sr-only"
                tabIndex={-1}
                aria-hidden
                onChange={async (e) => {
                  const f = Array.from(e.target.files ?? []);
                  e.target.value = '';
                  if (!f.length) return;
                  const r = await reselect(pausedKeys, f);
                  setLastResult(
                    `${r.resumed} resumed${r.rejected ? `, ${r.rejected} didn't match` : ''}${r.unmatched ? `, ${r.unmatched} not found in your selection` : ''}.`,
                  );
                }}
              />
              <Button variant="primary" block icon={<RefreshCw aria-hidden className="size-4" />} onClick={() => batchInput.current?.click()}>
                Reselect files ({pausedKeys.length})
              </Button>
            </>
          )}
          {sum.done > 0 && (
            <Button variant="outline" block onClick={clearFinished}>
              Clear finished
            </Button>
          )}
        </div>
      }
    >
      {all.length === 0 ? (
        <p className="py-8 text-center text-[15px] text-muted">No uploads right now.</p>
      ) : (
        <>
          {pausedKeys.length > 0 && (
            <p className="mb-2 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-ink">
              The app was closed or reloaded, so these files must be picked again to continue. Uploads don't continue while the app is closed.
            </p>
          )}
          {lastResult && <p className="mb-2 text-sm text-muted">{lastResult}</p>}
          {groups.map(([biz, list]) => (
            <section key={biz} aria-label={businessNames?.[biz] ?? 'Uploads'}>
              {groups.length > 1 && <h3 className="mt-3 text-sm font-semibold text-muted">{businessNames?.[biz] ?? 'Business'}</h3>}
              <ul>
                {list.map((it) => (
                  <Row key={it.key} it={it} />
                ))}
              </ul>
            </section>
          ))}
        </>
      )}
    </Sheet>
  );
}
